import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Suspension Add-On Modal
 *
 * Intercepts the standard product page add-to-cart when "Frame Only" mode
 * is active and a suspension_add_ons metafield collection exists.
 * Shows a dialog for the user to optionally add a suspension product
 * before the frame is added to cart.
 *
 * @extends DialogComponent
 */
class SuspensionModalComponent extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {{ variantId: string, quantity: string }|null} */
  #pendingAddToCart = null;

  /** @type {Function} Reference to parent's closeDialog */
  #parentCloseDialog;

  constructor() {
    super();
    // Capture the parent's closeDialog arrow function before overriding,
    // since arrow function class properties can't be reached via super.
    this.#parentCloseDialog = this.closeDialog;
    this.closeDialog = this.#closeDialogOverride;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', this.#handleButtonClick, {
      capture: true,
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  // ─── Public Methods (called via declarative on:* events) ──────────────

  /**
   * Handles clicking "Add to Cart" on a suspension product.
   * Adds the suspension product to cart, then adds the frame.
   * Called via: on:click="suspension-modal-component/addSuspension"
   * @param {Event} event
   */
  async addSuspension(event) {
    const btn = event.target.closest('[data-suspension-add-btn]');
    if (!btn) return;

    const productCard = btn.closest('[data-suspension-product]');
    const variantSelect = productCard?.querySelector('[data-suspension-variant-select]');
    const variantId = variantSelect ? variantSelect.value : btn.dataset.variantId;

    if (!variantId) return;

    btn.disabled = true;
    btn.textContent = 'Adding...';

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        }),
      });

      await this.#addFrameToCart();
      this.#parentCloseDialog();
      this.#openCartDrawer();
    } catch (error) {
      console.error('Failed to add suspension product:', error);
      btn.disabled = false;
      btn.textContent = 'Add to Cart';
    }
  }

  /**
   * Dismisses the modal and proceeds to add just the frame.
   * Called via: on:click="suspension-modal-component/dismiss"
   */
  async dismiss() {
    await this.#addFrameToCart();
    this.#parentCloseDialog();
    this.#openCartDrawer();
  }

  // ─── Private Methods ──────────────────────────────────────────────────

  /**
   * Override for closeDialog so backdrop clicks and Escape key
   * route through dismiss() to ensure the frame is always added.
   */
  #closeDialogOverride = async () => {
    if (this.#pendingAddToCart) {
      await this.dismiss();
      return;
    }
    this.#parentCloseDialog();
  };

  /**
   * Intercepts clicks on the add-to-cart button when frame-only mode is active.
   * Uses click instead of submit to fire before the Component framework's
   * submit handler, preventing the double add-to-cart race condition.
   * @param {MouseEvent} event
   */
  #handleButtonClick = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    const button = target?.closest('button[name="add"]');
    if (!button) return;

    const form = button.closest('form[data-type="add-to-cart-form"]');
    if (!form) return;

    if (!this.#isFrameOnlyMode()) return;

    // Prevent the button click's default action (form submission).
    // This ensures the submit event never fires, so ProductFormComponent.handleSubmit never runs.
    event.preventDefault();
    event.stopImmediatePropagation();

    const variantInput = form.querySelector('input[name="id"]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    const variantId = variantInput?.value;
    const quantity = quantityInput?.value || '1';

    if (!variantId) return;

    this.#pendingAddToCart = { variantId, quantity };
    this.showDialog();
  };

  /**
   * Opens the cart drawer if it exists and has auto-open enabled.
   */
  #openCartDrawer() {
    const cartDrawer = /** @type {HTMLElement & { showDialog?: Function }} */ (
      document.querySelector('cart-drawer-component[auto-open]')
    );
    cartDrawer?.showDialog?.();
  }

  /**
   * Checks if the bundle mode toggle is set to "frame-only".
   * @returns {boolean}
   */
  #isFrameOnlyMode() {
    return !!document.querySelector('input[data-bundle-mode="frame-only"]:checked');
  }

  /**
   * Adds the frame (parent product) to cart and dispatches CartAddEvent.
   */
  async #addFrameToCart() {
    if (!this.#pendingAddToCart) return;

    const { variantId, quantity } = this.#pendingAddToCart;
    this.#pendingAddToCart = null;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [
            {
              id: parseInt(variantId, 10),
              quantity: parseInt(quantity, 10) || 1,
            },
          ],
        }),
      });

      if (!response.ok) {
        console.error('Frame add to cart failed:', response.status);
        return;
      }

      const result = await response.json();

      this.dispatchEvent(
        new CartAddEvent(result, this.id || 'suspension-modal', {
          source: 'suspension-modal',
          itemCount: 1,
        })
      );
    } catch (error) {
      console.error('Frame add to cart error:', error);
    }
  }
}

if (!customElements.get('suspension-modal-component')) {
  customElements.define('suspension-modal-component', SuspensionModalComponent);
}
